import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate } from 'react-router-dom'
import { supabase, TowJob, Profile, TowerService, BusinessUser } from '../lib/supabase'
import { createTeamMember } from '../lib/teamService'
import { Modal, TeamMemberSuccessModal, ErrorModal, ConfirmModal } from '../components/Modal'
import jsPDF from 'jspdf'
import { UserRole } from '../types'
import { hasRole, getAllRoles } from '../lib/roleUtils'
import {
  LayoutDashboard, Truck, MapPin, DollarSign, Star, CheckCircle,
  Settings, Clock, Bell, Navigation, Users, TrendingUp, BarChart3,
  Activity, LogOut, Plus, Wrench, Calendar, Phone, MessageSquare, Mail,
  AlertCircle, CreditCard, FileText, Shield, Menu, X, User, ChevronDown, BarChart, Info, Download,
  TrendingUp as TrendingUpIcon, Zap, Droplet, Wrench as WrenchIcon, Fuel, Loader2, Building2, Trash2, Edit, BadgeCheck, Package
} from 'lucide-react'
import TowerPaymentConfirmation from '../components/TowerPaymentConfirmation'

type Tab = 'overview' | 'requests' | 'active' | 'payments' | 'history' | 'earnings' | 'profile' | 'settings' | 'reports' | 'rates' | 'services' | 'team'

export default function TowerDashboard() {
  const { profile, signOut, addRole, removeRole, session } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [roleError, setRoleError] = useState('')
  const [addingRole, setAddingRole] = useState(false)
  const [showAddRolesModal, setShowAddRolesModal] = useState(false)
  const [selectedRolesToAdd, setSelectedRolesToAdd] = useState<UserRole[]>([])
  const [showSwitchRoleModal, setShowSwitchRoleModal] = useState(false)
  const [showRemoveRoleConfirm, setShowRemoveRoleConfirm] = useState(false)
  const [roleToRemove, setRoleToRemove] = useState<UserRole | null>(null)
  const [showRoleRemovalSuccess, setShowRoleRemovalSuccess] = useState(false)
  const [roleRemovalSuccessData, setRoleRemovalSuccessData] = useState<{ role: string } | null>(null)
  const [showAddRoleConfirm, setShowAddRoleConfirm] = useState(false)
  const [showRoleAddSuccess, setShowRoleAddSuccess] = useState(false)
  const [roleAddSuccessData, setRoleAddSuccessData] = useState<{ roles: string[] } | null>(null)

  const userRoles = getAllRoles(profile)

  const availableRoles: { value: UserRole; label: string; icon: any; description: string }[] = [
    { value: 'garage', label: 'Garage Services', icon: Wrench, description: 'Offer vehicle repair and maintenance services' },
    { value: 'parts_vendor', label: 'Parts Sales', icon: Package, description: 'Sell vehicle parts and accessories' },
    { value: 'insurer', label: 'Insurance Provider', icon: Building2, description: 'Provide vehicle insurance services' },
  ]

  const handleAddRole = async (role: UserRole) => {
    setAddingRole(true)
    setRoleError('')
    const result = await addRole(role)
    if (result.error) {
      setRoleError(result.error)
    }
    setAddingRole(false)
  }

  const handleRemoveRole = async (role: UserRole) => {
    setRoleToRemove(role)
    setShowRemoveRoleConfirm(true)
  }

  const confirmRemoveRole = async () => {
    if (!roleToRemove) return
    setAddingRole(true)
    setRoleError('')
    const result = await removeRole(roleToRemove)
    if (result.error) {
      setRoleError(result.error)
      setShowRemoveRoleConfirm(false)
    } else {
      setShowRemoveRoleConfirm(false)
      setRoleRemovalSuccessData({ role: roleToRemove })
      setShowRoleRemovalSuccess(true)
      setTimeout(() => window.location.reload(), 1500)
    }
    setAddingRole(false)
  }

  const handleAddSelectedRoles = async () => {
    if (selectedRolesToAdd.length === 0) return
    setShowAddRoleConfirm(true)
  }

  const confirmAddRoles = async () => {
    if (selectedRolesToAdd.length === 0) return

    setAddingRole(true)
    setRoleError('')

    try {
      // Add all roles at once to database
      const { error } = await supabase.from('user_roles').insert(
        selectedRolesToAdd.map(role => ({
          user_id: session?.user.id,
          role: role
        }))
      )

      if (error) {
        console.error('Error adding roles:', error)
        setRoleError(`Failed to add roles: ${error.message}`)
        setShowAddRoleConfirm(false)
      } else {
        setShowAddRoleConfirm(false)
        setRoleAddSuccessData({ roles: selectedRolesToAdd })
        setShowRoleAddSuccess(true)
        setSelectedRolesToAdd([])
        setShowAddRolesModal(false)
        setTimeout(() => window.location.reload(), 1500)
      }
    } catch (e) {
      console.error('Exception adding roles:', e)
      setRoleError('Failed to add roles. Please try again.')
      setShowAddRoleConfirm(false)
    } finally {
      setAddingRole(false)
    }
  }

  const toggleRoleSelection = (role: UserRole) => {
    if (selectedRolesToAdd.includes(role)) {
      setSelectedRolesToAdd(prev => prev.filter(r => r !== role))
    } else {
      setSelectedRolesToAdd(prev => [...prev, role])
    }
  }
  const [jobs, setJobs] = useState<TowJob[]>([])
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [selectedPaymentJob, setSelectedPaymentJob] = useState<TowJob | null>(null)
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false)
  const [selectedJob, setSelectedJob] = useState<TowJob | null>(null)
  const [customerNames, setCustomerNames] = useState<Record<string, string>>({})
  const [showEditPersonalModal, setShowEditPersonalModal] = useState(false)
  const [showEditBusinessModal, setShowEditBusinessModal] = useState(false)
  const [showEditBankingModal, setShowEditBankingModal] = useState(false)
  const [editingProfile, setEditingProfile] = useState<Partial<Profile>>({})
  const [savingProfile, setSavingProfile] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [businessPhoneError, setBusinessPhoneError] = useState('')
  const [mpesaError, setMpesaError] = useState('')
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showEditRatesModal, setShowEditRatesModal] = useState(false)
  const [editingRates, setEditingRates] = useState({
    base_towing_rate: 0,
    per_km_rate: 0,
    battery_service_rate: 0,
    flat_tyre_rate: 0,
    fuel_delivery_base_rate: 0,
    fuel_delivery_per_km_rate: 0
  })
  const [serviceRates, setServiceRates] = useState({
    base_towing_rate: profile?.base_towing_rate || 1500,
    per_km_rate: profile?.per_km_rate || 100,
    battery_service_rate: profile?.battery_service_rate || 2000,
    flat_tyre_rate: profile?.flat_tyre_rate || 1500,
    fuel_delivery_base_rate: profile?.fuel_delivery_base_rate || 500,
    fuel_delivery_per_km_rate: profile?.fuel_delivery_per_km_rate || 50
  })
  const [savingRates, setSavingRates] = useState(false)
  const [marketRates, setMarketRates] = useState({
    base_towing_rate: { min: 0, max: 0, avg: 0 },
    per_km_rate: { min: 0, max: 0, avg: 0 },
    battery_service_rate: { min: 0, max: 0, avg: 0 },
    flat_tyre_rate: { min: 0, max: 0, avg: 0 },
    fuel_delivery_base_rate: { min: 0, max: 0, avg: 0 },
    fuel_delivery_per_km_rate: { min: 0, max: 0, avg: 0 }
  })
  const [loadingMarketRates, setLoadingMarketRates] = useState(false)
  const [towerServices, setTowerServices] = useState<TowerService[]>([])
  const [showAddServiceModal, setShowAddServiceModal] = useState(false)
  const [editingService, setEditingService] = useState<TowerService | null>(null)
  const [newService, setNewService] = useState({
    service_name: '',
    description: '',
    base_price: 0,
    per_km_rate: 0,
    requires_destination: true,
    requires_vehicle: true,
    is_active: true,
    vehicle_types: [] as string[]
  })
  const [savingService, setSavingService] = useState(false)
  
  // Team management state
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false)
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    full_name: '',
    role: 'staff' as 'admin' | 'staff'
  })
  const [addingTeamMember, setAddingTeamMember] = useState(false)
  const [showTeamSuccessModal, setShowTeamSuccessModal] = useState(false)
  const [teamSuccessModalData, setTeamSuccessModalData] = useState({
    email: '',
    fullName: '',
    tempPassword: '',
    role: '',
    businessName: '',
    emailSent: false
  })
  const [showTeamErrorModal, setShowTeamErrorModal] = useState(false)
  const [teamErrorModalData, setTeamErrorModalData] = useState({
    title: '',
    message: ''
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: () => {}
  })

  useEffect(() => {
    if (!profile) return
    supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => {
      setJobs((data as TowJob[]) || [])
      // Load customer names for jobs
      const customerIds = [...new Set((data as TowJob[]).map(j => j.customer_id))]
      customerIds.forEach(async (customerId) => {
        const { data: profile } = await supabase.from('profiles').select('full_name').eq('id', customerId).single()
        if (profile) {
          setCustomerNames(prev => ({ ...prev, [customerId]: profile.full_name }))
        }
      })
    })
  }, [profile])

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Load team members
  useEffect(() => {
    if (!profile) return
    loadTeamMembers()
  }, [profile])

  const loadTeamMembers = async () => {
    if (!profile) return
    const { data } = await supabase
      .from('business_users')
      .select('*')
      .eq('business_id', profile.id)
    
    // Fetch profiles for each team member
    const userIds = (data || []).map(m => m.user_id)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone')
      .in('id', userIds)
    
    const profilesMap = (profiles || []).reduce((acc, p) => {
      acc[p.id] = p
      return acc
    }, {} as Record<string, any>)
    
    const teamMembersWithProfiles = (data || []).map(m => ({
      ...m,
      profiles: profilesMap[m.user_id] || null
    }))
    
    setTeamMembers(teamMembersWithProfiles)
  }

  const addTeamMember = async () => {
    if (!profile) return
    setAddingTeamMember(true)

    try {
      // Call server-side API to create team member (prevents session switching)
      const result = await createTeamMember({
        email: newTeamMember.email,
        fullName: newTeamMember.full_name,
        role: newTeamMember.role,
        businessId: profile.id,
        businessName: profile?.business_name || profile?.full_name || 'Your Business',
        businessType: 'tower'
      })

      if (!result.success) {
        setTeamErrorModalData({
          title: 'Team Member Creation Failed',
          message: result.message
        })
        setShowTeamErrorModal(true)
        setAddingTeamMember(false)
        return
      }

      // Refresh team members
      await loadTeamMembers()

      // Reset form and close modal
      setNewTeamMember({ email: '', full_name: '', role: 'staff' })
      setShowAddTeamMemberModal(false)

      // Show success modal
      setTeamSuccessModalData({
        email: newTeamMember.email,
        fullName: newTeamMember.full_name,
        tempPassword: result.tempPassword || '',
        role: newTeamMember.role,
        businessName: profile?.business_name || profile?.full_name || 'Your Business',
        emailSent: result.emailSent
      })
      setShowTeamSuccessModal(true)

    } catch (error) {
      setTeamErrorModalData({
        title: 'Error Occurred',
        message: `An unexpected error occurred: ${(error as any).message}`
      })
      setShowTeamErrorModal(true)
    } finally {
      setAddingTeamMember(false)
    }
  }

  const removeTeamMember = async (userId: string) => {
    if (!profile) return
    
    setConfirmModalData({
      title: 'Remove Team Member',
      message: 'Are you sure you want to remove this team member? This action cannot be undone.',
      onConfirm: async () => {
        const { error } = await supabase
          .from('business_users')
          .delete()
          .eq('business_id', profile.id)
          .eq('user_id', userId)
        
        if (error) {
          setTeamErrorModalData({
            title: 'Removal Failed',
            message: `Error removing team member: ${error.message}`
          })
          setShowTeamErrorModal(true)
          return
        }
        
        await loadTeamMembers()
      }
    })
    setShowConfirmModal(true)
  }

  const resendInvitation = async (member: any) => {
    if (!profile) return

    setTeamErrorModalData({
      title: 'Feature Not Available',
      message: 'Password reset functionality coming soon. Please contact the team member directly or remove and re-add them.'
    })
    setShowTeamErrorModal(true)
  }

  // Load market rates from other towers
  useEffect(() => {
    if (!profile) return
    loadMarketRates()
  }, [profile])

  // Load tower services
  useEffect(() => {
    if (!profile) return
    loadTowerServices()
  }, [profile])

  const loadTowerServices = async () => {
    const { data, error } = await supabase
      .from('tower_services')
      .select('*')
      .eq('tower_id', profile?.id)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error loading tower services:', error)
      return
    }

    setTowerServices((data as TowerService[]) || [])
  }

  const loadMarketRates = async () => {
    setLoadingMarketRates(true)
    try {
      const { data: towers, error } = await supabase
        .from('profiles')
        .select('base_towing_rate, per_km_rate, battery_service_rate, flat_tyre_rate, fuel_delivery_base_rate, fuel_delivery_per_km_rate')
        .eq('role', 'tower')
        .not('base_towing_rate', 'is', null)

      if (error || !towers || towers.length === 0) {
        // Use default market rates if no data
        setMarketRates({
          base_towing_rate: { min: 1000, max: 2000, avg: 1500 },
          per_km_rate: { min: 60, max: 120, avg: 80 },
          battery_service_rate: { min: 1500, max: 3000, avg: 2000 },
          flat_tyre_rate: { min: 1000, max: 2000, avg: 1500 },
          fuel_delivery_base_rate: { min: 300, max: 800, avg: 500 },
          fuel_delivery_per_km_rate: { min: 30, max: 80, avg: 50 }
        })
        return
      }

      // Calculate market averages
      const calculateStats = (field: string) => {
        const values = towers.map((t: any) => t[field]).filter((v: any) => v !== null && v !== undefined)
        if (values.length === 0) return { min: 0, max: 0, avg: 0 }
        const min = Math.min(...values)
        const max = Math.max(...values)
        const avg = values.reduce((sum: number, v: number) => sum + v, 0) / values.length
        return { min, max, avg: Math.round(avg) }
      }

      setMarketRates({
        base_towing_rate: calculateStats('base_towing_rate'),
        per_km_rate: calculateStats('per_km_rate'),
        battery_service_rate: calculateStats('battery_service_rate'),
        flat_tyre_rate: calculateStats('flat_tyre_rate'),
        fuel_delivery_base_rate: calculateStats('fuel_delivery_base_rate'),
        fuel_delivery_per_km_rate: calculateStats('fuel_delivery_per_km_rate')
      })
    } catch (error) {
      console.error('Error loading market rates:', error)
    } finally {
      setLoadingMarketRates(false)
    }
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as HTMLElement).closest('.user-menu-container')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  const myJobs = jobs.filter(j => j.selected_tower_id === profile?.id || j.operator_id === profile?.id)
  const pendingJobs = myJobs.filter(j => j.status === 'pending')
  const activeJobs = myJobs.filter(j => ['dispatched', 'en_route', 'arrived', 'towing'].includes(j.status))
  const completedJobs = myJobs.filter(j => j.status === 'completed')
  const paidJobs = myJobs.filter(j => j.status === 'payment_completed')
  const allCompletedJobs = [...completedJobs, ...paidJobs]
  const awaitingPayment = completedJobs.filter(j => j.status === 'completed')
  const totalEarnings = paidJobs.reduce((sum, job) => sum + Number(job.total_amount), 0)

  // Debug logging
  console.log('Tower Profile ID:', profile?.id)
  console.log('All Jobs:', jobs.length)
  console.log('My Jobs (filtered):', myJobs.length)
  console.log('Pending Jobs:', pendingJobs.length)
  console.log('Active Jobs:', activeJobs.length)
  myJobs.forEach(job => {
    console.log(`Job ${job.reference}: selected_tower_id=${job.selected_tower_id}, operator_id=${job.operator_id}, status=${job.status}`)
  })

  const acceptJob = async (jobId: string) => {
    const { error } = await supabase
      .from('tow_jobs')
      .update({ status: 'dispatched' })
      .eq('id', jobId)
    
    if (error) {
      alert('Failed to accept job: ' + error.message)
      return
    }
    
    // Reload jobs
    supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => setJobs((data as TowJob[]) || []))
  }

  const declineJob = async (jobId: string) => {
    const { error } = await supabase
      .from('tow_jobs')
      .update({ status: 'cancelled', operator_id: null, selected_tower_id: null })
      .eq('id', jobId)
    
    if (error) {
      alert('Failed to decline job: ' + error.message)
      return
    }
    
    // Reload jobs
    supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => setJobs((data as TowJob[]) || []))
  }

  const updateJobStatus = async (jobId: string, newStatus: string) => {
    const updateData: any = { status: newStatus }

    // If marking as completed, add payment record to diagnosis_notes
    if (newStatus === 'completed') {
      const timestamp = new Date().toISOString()
      const paymentRef = `PAY-${Date.now().toString().slice(-8)}`
      const docNumber = `DOC-${Date.now().toString().slice(-8)}`
      updateData.diagnosis_notes = `PAID|${paymentRef}|${docNumber}|${timestamp}|Job completed by tower`
      updateData.completed_at = timestamp
    }

    const { error } = await supabase
      .from('tow_jobs')
      .update(updateData)
      .eq('id', jobId)

    if (error) {
      alert('Failed to update status: ' + error.message)
      return
    }

    // Reload jobs
    supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => setJobs((data as TowJob[]) || []))
  }

  const [showStatusConfirm, setShowStatusConfirm] = useState(false)
  const [confirmingJobId, setConfirmingJobId] = useState<string | null>(null)
  const [confirmingStatus, setConfirmingStatus] = useState<string | null>(null)

  const handleStatusUpdate = (jobId: string, newStatus: string) => {
    setConfirmingJobId(jobId)
    setConfirmingStatus(newStatus)
    setShowStatusConfirm(true)
  }

  const confirmStatusUpdate = async () => {
    if (confirmingJobId && confirmingStatus) {
      await updateJobStatus(confirmingJobId, confirmingStatus)
      setShowStatusConfirm(false)
      setConfirmingJobId(null)
      setConfirmingStatus(null)
    }
  }

  const saveServiceRates = async () => {
    setSavingRates(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          base_towing_rate: serviceRates.base_towing_rate,
          per_km_rate: serviceRates.per_km_rate,
          battery_service_rate: serviceRates.battery_service_rate,
          flat_tyre_rate: serviceRates.flat_tyre_rate,
          fuel_delivery_base_rate: serviceRates.fuel_delivery_base_rate,
          fuel_delivery_per_km_rate: serviceRates.fuel_delivery_per_km_rate,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile?.id)

      if (error) {
        alert('Failed to save rates: ' + error.message)
        return
      }

      // Show success modal
      setShowSuccessModal(true)

      // Reload data after delay
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (error) {
      alert('Failed to save rates: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setSavingRates(false)
    }
  }

  const openEditRatesModal = () => {
    setEditingRates({
      base_towing_rate: serviceRates.base_towing_rate,
      per_km_rate: serviceRates.per_km_rate,
      battery_service_rate: serviceRates.battery_service_rate,
      flat_tyre_rate: serviceRates.flat_tyre_rate,
      fuel_delivery_base_rate: serviceRates.fuel_delivery_base_rate,
      fuel_delivery_per_km_rate: serviceRates.fuel_delivery_per_km_rate
    })
    setShowEditRatesModal(true)
  }

  const saveRatesFromModal = async () => {
    setSavingRates(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          base_towing_rate: editingRates.base_towing_rate,
          per_km_rate: editingRates.per_km_rate,
          battery_service_rate: editingRates.battery_service_rate,
          flat_tyre_rate: editingRates.flat_tyre_rate,
          fuel_delivery_base_rate: editingRates.fuel_delivery_base_rate,
          fuel_delivery_per_km_rate: editingRates.fuel_delivery_per_km_rate,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile?.id)

      if (error) {
        alert('Failed to save rates: ' + error.message)
        return
      }

      // Update local state
      setServiceRates(editingRates)
      setShowEditRatesModal(false)

      // Show success modal
      setShowSuccessModal(true)

      // Reload data after delay
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (error) {
      alert('Failed to save rates: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setSavingRates(false)
    }
  }

  const getRateStatus = (rate: number, market: { min: number; max: number; avg: number }) => {
    // If no market data yet, return neutral
    if (market.avg === 0) return { status: 'neutral', message: 'No market data available yet' }
    if (rate < market.min * 0.8) return { status: 'low', message: 'Below market - may not be profitable' }
    if (rate > market.max * 1.2) return { status: 'high', message: 'Above market - may lose customers' }
    if (rate >= market.min && rate <= market.max) return { status: 'good', message: 'Within market range' }
    return { status: 'neutral', message: 'Reasonable rate' }
  }

  const openEditPersonal = () => {
    setEditingProfile({
      full_name: profile?.full_name,
      phone: profile?.phone,
      truck_type: profile?.truck_type,
      truck_plate: profile?.truck_plate
    })
    setShowEditPersonalModal(true)
  }

  const openEditBusiness = () => {
    setEditingProfile({
      business_name: profile?.business_name,
      business_registration: profile?.business_registration,
      tax_pin: profile?.tax_pin,
      business_address: profile?.business_address,
      business_phone: profile?.business_phone,
      business_email: profile?.business_email
    })
    setShowEditBusinessModal(true)
  }

  const openEditBanking = () => {
    setEditingProfile({
      bank_name: profile?.bank_name,
      bank_account_number: profile?.bank_account_number,
      mpesa_number: profile?.mpesa_number
    })
    setShowEditBankingModal(true)
  }

  const openAddServiceModal = () => {
    setNewService({
      service_name: '',
      description: '',
      base_price: 0,
      per_km_rate: 0,
      requires_destination: true,
      requires_vehicle: true,
      is_active: true,
      vehicle_types: []
    })
    setEditingService(null)
    setShowAddServiceModal(true)
  }

  const openEditServiceModal = (service: TowerService) => {
    setNewService({
      service_name: service.service_name,
      description: service.description || '',
      base_price: service.base_price,
      per_km_rate: service.per_km_rate,
      requires_destination: service.requires_destination,
      requires_vehicle: service.requires_vehicle,
      is_active: service.is_active,
      vehicle_types: service.vehicle_types || []
    })
    setEditingService(service)
    setShowAddServiceModal(true)
  }

  const saveService = async () => {
    setSavingService(true)
    try {
      if (editingService) {
        // Update existing service
        const { error } = await supabase
          .from('tower_services')
          .update({
            service_name: newService.service_name,
            description: newService.description,
            base_price: newService.base_price,
            per_km_rate: newService.per_km_rate,
            requires_destination: newService.requires_destination,
            requires_vehicle: newService.requires_vehicle,
            is_active: newService.is_active,
            vehicle_types: newService.vehicle_types,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingService.id)

        if (error) {
          alert('Failed to update service: ' + error.message)
          return
        }
      } else {
        // Create new service
        const { error } = await supabase
          .from('tower_services')
          .insert({
            tower_id: profile?.id,
            service_name: newService.service_name,
            description: newService.description,
            base_price: newService.base_price,
            per_km_rate: newService.per_km_rate,
            requires_destination: newService.requires_destination,
            requires_vehicle: newService.requires_vehicle,
            is_active: newService.is_active,
            vehicle_types: newService.vehicle_types
          })

        if (error) {
          alert('Failed to add service: ' + error.message)
          return
        }
      }

      setShowAddServiceModal(false)
      setEditingService(null)
      loadTowerServices()
    } catch (error) {
      alert('Failed to save service: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setSavingService(false)
    }
  }

  const deleteService = async (serviceId: string) => {
    if (!confirm('Are you sure you want to delete this service?')) return

    const { error } = await supabase
      .from('tower_services')
      .delete()
      .eq('id', serviceId)

    if (error) {
      alert('Failed to delete service: ' + error.message)
      return
    }

    loadTowerServices()
  }

  const saveProfileChanges = async () => {
    setSavingProfile(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...editingProfile,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile?.id)

      if (error) {
        alert('Failed to save changes: ' + error.message)
        return
      }

      // Close all modals
      setShowEditPersonalModal(false)
      setShowEditBusinessModal(false)
      setShowEditBankingModal(false)

      setPhoneError('')
      setBusinessPhoneError('')
      setMpesaError('')

      // Show success modal
      setShowSuccessModal(true)

      // Reload profile data after a delay
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (error) {
      alert('Failed to save changes: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setSavingProfile(false)
    }
  }

  const validatePhoneNumber = (phone: string): boolean => {
    // Validate Kenyan phone numbers: +254XXXXXXXXX or 07XXXXXXXXX or 01XXXXXXXXX
    const phoneRegex = /^(\+254|0|1)[0-9]{8,9}$/
    return phoneRegex.test(phone.replace(/\s/g, ''))
  }

  const handlePhoneChange = (value: string) => {
    // Only allow numbers and + sign
    const numericValue = value.replace(/[^0-9+]/g, '')
    setEditingProfile({ ...editingProfile, phone: numericValue })
    if (numericValue && !validatePhoneNumber(numericValue)) {
      setPhoneError('Please enter a valid Kenyan phone number (e.g., +2547XXXXXXXXX or 07XXXXXXXXX)')
    } else {
      setPhoneError('')
    }
  }

  const handleBusinessPhoneChange = (value: string) => {
    // Only allow numbers and + sign
    const numericValue = value.replace(/[^0-9+]/g, '')
    setEditingProfile({ ...editingProfile, business_phone: numericValue })
    if (numericValue && !validatePhoneNumber(numericValue)) {
      setBusinessPhoneError('Please enter a valid Kenyan phone number (e.g., +2547XXXXXXXXX or 07XXXXXXXXX)')
    } else {
      setBusinessPhoneError('')
    }
  }

  const handleMpesaChange = (value: string) => {
    // Only allow numbers and + sign
    const numericValue = value.replace(/[^0-9+]/g, '')
    setEditingProfile({ ...editingProfile, mpesa_number: numericValue })
    if (numericValue && !validatePhoneNumber(numericValue)) {
      setMpesaError('Please enter a valid Kenyan phone number (e.g., +2547XXXXXXXXX or 07XXXXXXXXX)')
    } else {
      setMpesaError('')
    }
  }

  const getNextStatus = (currentStatus: string) => {
    const statusFlow: Record<string, string[]> = {
      'dispatched': ['en_route'],
      'en_route': ['completed']
    }
    return statusFlow[currentStatus] || []
  }

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <span>Mk</span>ononi
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '1.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text)',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Overview</div>
            <div className={`sidebar-item ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Jobs</div>
            <div className={`sidebar-item ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>
              <Truck size={20} />
              <span>Job Requests</span>
              {pendingJobs.length > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--red)',
                  color: 'white',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}>
                  {pendingJobs.length}
                </span>
              )}
            </div>
            <div className={`sidebar-item ${tab === 'active' ? 'active' : ''}`} onClick={() => setTab('active')}>
              <Navigation size={20} />
              <span>Active Jobs</span>
              {activeJobs.length > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--blue)',
                  color: 'white',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}>
                  {activeJobs.length}
                </span>
              )}
            </div>
            <div className={`sidebar-item ${tab === 'payments' ? 'active' : ''}`} onClick={() => setTab('payments')}>
              <CreditCard size={20} />
              <span>Payment Confirmations</span>
              {awaitingPayment.length > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  background: '#f59e0b',
                  color: 'white',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}>
                  {awaitingPayment.length}
                </span>
              )}
            </div>
            <div className={`sidebar-item ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
              <Clock size={20} />
              <span>History</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Financial</div>
            <div className={`sidebar-item ${tab === 'earnings' ? 'active' : ''}`} onClick={() => setTab('earnings')}>
              <DollarSign size={20} />
              <span>Earnings</span>
            </div>
            <div className="sidebar-item" onClick={() => navigate('/payments')}>
              <CreditCard size={20} />
              <span>Payments</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Reports</div>
            <div className={`sidebar-item ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>
              <BarChart size={20} />
              <span>Reports</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Account</div>
            <div className={`sidebar-item ${tab === 'profile' ? 'active' : ''}`} onClick={() => setTab('profile')}>
              <Users size={20} />
              <span>Profile</span>
            </div>
            <div className={`sidebar-item ${tab === 'rates' ? 'active' : ''}`} onClick={() => setTab('rates')}>
              <DollarSign size={20} />
              <span>Service Rates</span>
            </div>
            <div className={`sidebar-item ${tab === 'services' ? 'active' : ''}`} onClick={() => setTab('services')}>
              <Wrench size={20} />
              <span>My Services</span>
            </div>
            <div className={`sidebar-item ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}>
              <Users size={20} />
              <span>Team</span>
            </div>
            <div className={`sidebar-item ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
              <Settings size={20} />
              <span>Settings</span>
            </div>
          </div>
        </nav>
        <div className="sidebar-bottom-fixed">
          <div className="sidebar-footer">
            <div className="name">{profile?.full_name || 'Tower'}</div>
            <div className="role">{profile?.role || 'tower'}</div>
            <button className="sign-out" onClick={signOut}>
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
          className="mobile-overlay"
        />
      )}

      {/* Main Content */}
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'var(--text)'
              }}
            >
              <Menu size={24} />
            </button>
            <div>
              <h1>
                {tab === 'overview' && 'Tower Dashboard'}
                {tab === 'requests' && 'Job Requests'}
                {tab === 'active' && 'Active Jobs'}
                {tab === 'history' && 'Job History'}
                {tab === 'earnings' && 'Earnings'}
                {tab === 'profile' && 'Profile'}
                {tab === 'reports' && 'Reports'}
                {tab === 'rates' && 'Service Rates'}
                {tab === 'services' && 'My Services'}
                {tab === 'team' && 'Team'}
                {tab === 'settings' && 'Settings'}
              </h1>
              <p className="subtitle">
                Welcome back, {profile?.full_name?.split(' ')[0] || 'Tower'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            {/* Switch Role Button - Only show if multi-role */}
            {userRoles.length > 1 && (
              <button
                onClick={() => setShowSwitchRoleModal(true)}
                className="btn btn-outline"
                style={{ padding: '0.5rem 1rem', gap: '0.5rem' }}
              >
                <BadgeCheck size={18} />
                <span className="desktop-only" style={{ marginLeft: '0.5rem' }}>Switch Role</span>
              </button>
            )}

            {/* Notifications Button */}
            <button className="btn btn-outline" style={{ padding: '0.5rem' }}>
              <Bell size={18} />
              <span className="desktop-only" style={{ marginLeft: '0.5rem' }}>Notifications</span>
            </button>

            {/* Account Status Banner - Desktop Only */}
            {profile?.verification_status === 'pending' && (
              <div className="desktop-only" style={{
                background: 'var(--amber-light)',
                border: '1px solid var(--amber)',
                borderRadius: '8px',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle size={16} color="var(--amber)" />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--amber)' }}>
                    Pending Approval
                  </div>
                </div>
              </div>
            )}

            <div className="user-menu-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '0.8rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <User size={16} />
                <span className="desktop-only">{profile?.full_name?.split(' ')[0] || 'Tower'}</span>
                <ChevronDown size={14} />
              </button>
              {showUserMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={() => { setTab('profile'); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--text)',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <User size={16} />
                    Profile
                  </button>
                  <button
                    onClick={() => { signOut(); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          {tab === 'overview' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                    <Truck size={24} />
                  </div>
                  <div className="stat-label">Pending Requests</div>
                  <div className="stat-value">{pendingJobs.length}</div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Awaiting acceptance
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>
                    <Navigation size={24} />
                  </div>
                  <div className="stat-label">Active Jobs</div>
                  <div className="stat-value">{activeJobs.length}</div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Currently in progress
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Completed Jobs</div>
                  <div className="stat-value">{completedJobs.length}</div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    Total completed
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-label">Total Earnings</div>
                  <div className="stat-value">KES {totalEarnings.toLocaleString()}</div>
                  <div className="stat-trend">
                    <BarChart3 size={14} />
                    All time revenue
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div className="card">
                  <h2 className="card-title">Pending Requests</h2>
                  {pendingJobs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
                      <Truck size={32} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
                      <p>No pending requests</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {pendingJobs.slice(0, 3).map(job => (
                        <div
                          key={job.id}
                          style={{
                            padding: '1rem',
                            background: 'var(--bg)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{job.reference}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                              {job.pickup_address}
                            </div>
                          </div>
                          <button
                            onClick={() => setTab('requests')}
                            className="btn btn-primary btn-sm"
                          >
                            View
                          </button>
                        </div>
                      ))}
                      {pendingJobs.length > 3 && (
                        <button
                          onClick={() => setTab('requests')}
                          className="btn btn-primary btn-sm"
                          style={{ width: '100%' }}
                        >
                          View All ({pendingJobs.length})
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="card">
                  <h2 className="card-title">Quick Actions</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <button
                      onClick={() => setTab('requests')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <Truck size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Requests</span>
                    </button>
                    <button
                      onClick={() => setTab('active')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <Navigation size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Active Jobs</span>
                    </button>
                    <button
                      onClick={() => setTab('earnings')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <DollarSign size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Earnings</span>
                    </button>
                    <button
                      onClick={() => navigate('/payments')}
                      className="btn btn-outline"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem' }}
                    >
                      <CreditCard size={24} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Payments</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="card-title">Recent Activity</h2>
                {myJobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                    <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No recent activity</p>
                  </div>
                ) : (
                  <>
                    <div className="data-table-wrapper desktop-only">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Reference</th>
                            <th>Service Type</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myJobs.slice(0, 5).map(job => (
                            <tr key={job.id}>
                              <td style={{ fontWeight: 600 }}>{job.reference}</td>
                              <td>{job.service_type.replace('_', ' ')}</td>
                              <td>
                                <span className={`badge ${
                                  job.status === 'payment_completed' ? 'badge-green' :
                                  job.status === 'cancelled' ? 'badge-red' :
                                  'badge-blue'
                                }`}>
                                  {job.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td>{new Date(job.created_at).toLocaleDateString()}</td>
                              <td style={{ fontWeight: 600 }}>
                                KES {Number(job.total_amount).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mobile-only" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {myJobs.slice(0, 5).map(job => (
                        <div
                          key={job.id}
                          style={{
                            padding: '0.75rem',
                            background: 'var(--bg)',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{job.reference}</span>
                            <span className={`badge ${
                              job.status === 'payment_completed' ? 'badge-green' :
                              job.status === 'cancelled' ? 'badge-red' :
                              'badge-blue'
                            }`} style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}>
                              {job.status.replace('_', ' ')}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                            {job.service_type.replace('_', ' ')}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                            <span style={{ color: 'var(--muted)' }}>{new Date(job.created_at).toLocaleDateString()}</span>
                            <span style={{ fontWeight: 600 }}>KES {Number(job.total_amount).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {tab === 'requests' && (
            <>
              {/* Stats */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                    <Truck size={24} />
                  </div>
                  <div className="stat-label">Pending Requests</div>
                  <div className="stat-value">{pendingJobs.length}</div>
                  <div className="stat-trend">
                    <AlertCircle size={14} />
                    Awaiting your response
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>
                    <Navigation size={24} />
                  </div>
                  <div className="stat-label">Active Jobs</div>
                  <div className="stat-value">{activeJobs.length}</div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Currently in progress
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Completed Today</div>
                  <div className="stat-value">
                    {completedJobs.filter(j => new Date(j.created_at).toDateString() === new Date().toDateString()).length}
                  </div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    Jobs finished today
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-label">Today's Earnings</div>
                  <div className="stat-value">
                    KES {completedJobs
                      .filter(j => new Date(j.created_at).toDateString() === new Date().toDateString())
                      .reduce((sum, j) => sum + Number(j.total_amount), 0)
                      .toLocaleString()}
                  </div>
                  <div className="stat-trend">
                    <BarChart3 size={14} />
                    Revenue today
                  </div>
                </div>
              </div>

              {/* Pending Jobs */}
              <div className="card" style={{ marginBottom: '2rem' }}>
                <h2 className="card-title">Pending Job Requests</h2>
                {pendingJobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                    <Truck size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No pending job requests</p>
                    <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      New requests will appear here
                    </p>
                  </div>
                ) : (
                  <div className="tower-jobs-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                    {pendingJobs.map(job => (
                      <div
                        key={job.id}
                        style={{
                          padding: '0.5rem',
                          background: 'white',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Truck size={14} color="white" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {job.reference}
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: '0.65rem', color: 'var(--muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <MapPin size={8} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.pickup_address}</span>
                        </div>
                        <div style={{ fontSize: '0.6rem', color: 'var(--muted)', marginBottom: '0.375rem' }}>
                          {job.service_type.replace('_', ' ')} • {job.operator_distance_km || 'N/A'} km
                        </div>
                        <div style={{
                          padding: '0.375rem',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          borderRadius: '6px',
                          marginBottom: '0.375rem',
                          border: '1px solid #fcd34d',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#78350f' }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.375rem' }}>
                          <button
                            className="btn"
                            onClick={() => acceptJob(job.id)}
                            style={{
                              flex: 1,
                              padding: '0.375rem',
                              borderRadius: '4px',
                              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                              border: 'none',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              boxShadow: '0 1px 3px rgba(220, 38, 38, 0.2)'
                            }}
                          >
                            Accept
                          </button>
                          <button
                            className="btn btn-outline"
                            onClick={() => declineJob(job.id)}
                            style={{
                              padding: '0.375rem 0.5rem',
                              borderRadius: '4px',
                              borderColor: 'var(--border)',
                              color: 'var(--text)',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Active Jobs */}
              {activeJobs.length > 0 && (
                <div className="card">
                  <h2 className="card-title">Active Jobs</h2>
                  <div className="tower-jobs-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                    {activeJobs.map(job => (
                      <div
                        key={job.id}
                        style={{
                          padding: '0.75rem',
                          background: 'white',
                          borderRadius: '12px',
                          border: '2px solid var(--border)',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Navigation size={18} color="white" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.reference}</div>
                              <div style={{ fontSize: '0.65rem', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {job.pickup_address}
                              </div>
                            </div>
                          </div>
                          <span className="badge badge-blue" style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem', flexShrink: 0 }}>
                            {job.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <button className="btn btn-primary btn-sm" style={{ 
                            flex: 1, 
                            padding: '0.4rem', 
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.2rem'
                          }}>
                            <Phone size={10} />
                            Call
                          </button>
                          <button className="btn btn-primary btn-sm" style={{ 
                            flex: 1, 
                            padding: '0.4rem', 
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.2rem'
                          }}>
                            <MessageSquare size={10} />
                            Message
                          </button>
                          {getNextStatus(job.status).map(nextStatus => (
                            <button
                              key={nextStatus}
                              className="btn"
                              style={{ 
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.2rem',
                                padding: '0.4rem',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer'
                              }}
                              onClick={() => updateJobStatus(job.id, nextStatus)}
                            >
                              {nextStatus.replace('_', ' ').toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'active' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Active Jobs</h2>
              {activeJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Navigation size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No active jobs</p>
                  <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    Accepted jobs will appear here
                  </p>
                </div>
              ) : (
                <div className="tower-jobs-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                  {activeJobs.map(job => (
                    <div
                      key={job.id}
                      style={{
                        padding: '0.5rem',
                        background: 'white',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Navigation size={14} color="white" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {job.reference}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--muted)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <MapPin size={8} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.pickup_address}</span>
                      </div>
                      <div style={{
                        padding: '0.25rem 0.375rem',
                        background: '#dbeafe',
                        borderRadius: '4px',
                        marginBottom: '0.375rem',
                        textAlign: 'center',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        color: '#1e40af'
                      }}>
                        {job.status.replace('_', ' ').toUpperCase()}
                      </div>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', marginTop: '0.375rem' }}>
                        {getNextStatus(job.status).map(nextStatus => (
                          <button
                            key={nextStatus}
                            className="btn"
                            style={{
                              padding: '0.5rem 0.75rem',
                              borderRadius: '4px',
                              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                              border: 'none',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.25rem'
                            }}
                            onClick={() => handleStatusUpdate(job.id, nextStatus)}
                          >
                            <CheckCircle size={12} />
                            {nextStatus === 'en_route' ? 'Start Job' : 'Complete'}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'payments' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Payment Confirmations</h2>
              {awaitingPayment.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <CreditCard size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No completed jobs awaiting payment</p>
                  <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    When you complete a job, it will appear here for payment confirmation
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {awaitingPayment.map(job => (
                    <div
                      key={job.id}
                      style={{
                        padding: '1.5rem',
                        background: 'var(--bg)',
                        borderRadius: 'var(--radius)',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: 'var(--blue-light)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--blue)',
                          }}>
                            <CreditCard size={24} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{job.reference}</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              {customerNames[job.customer_id] || 'Customer'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Amount</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#10b981' }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Completed At</div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                            {job.completed_at ? new Date(job.completed_at).toLocaleString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-green"
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            fontWeight: 600,
                            background: '#10b981'
                          }}
                          onClick={async () => {
                            setSelectedPaymentJob(job)
                            // Load customer name
                            const { data: profile } = await supabase.from('profiles').select('full_name').eq('id', job.customer_id).single()
                            if (profile) {
                              setCustomerNames(prev => ({ ...prev, [job.customer_id]: profile.full_name }))
                            }
                            setShowPaymentModal(true)
                          }}
                        >
                          <CheckCircle size={14} />
                          Mark as Paid
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'history' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Job History</h2>
              {allCompletedJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Clock size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No completed jobs yet</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  {!isMobile && (
                    <div className="data-table-wrapper">
                      <table className="data-table">
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Service Type</th>
                          <th>Pickup Location</th>
                          <th>Completed Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allCompletedJobs.map(job => (
                          <tr key={job.id}>
                            <td style={{ fontWeight: 600 }}>{job.reference}</td>
                            <td>{job.service_type.replace('_', ' ')}</td>
                            <td>{job.pickup_address}</td>
                            <td>{job.completed_at ? new Date(job.completed_at).toLocaleDateString() : new Date(job.created_at).toLocaleDateString()}</td>
                            <td style={{ fontWeight: 600 }}>
                              KES {Number(job.total_amount).toLocaleString()}
                            </td>
                            <td>
                              <span className={`badge ${job.status === 'payment_completed' ? 'badge-green' : 'badge-blue'}`}>
                                {job.status === 'payment_completed' ? 'PAID' : 'AWAITING PAYMENT'}
                              </span>
                            </td>
                            <td>
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                  setSelectedJob(job)
                                  setShowJobDetailsModal(true)
                                }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}

                  {/* Mobile Cards */}
                  {isMobile && (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {allCompletedJobs.map(job => (
                        <div
                          key={job.id}
                          style={{
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            padding: '0.75rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                            <div>
                              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
                                {job.reference}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                                {job.service_type.replace('_', ' ')}
                              </div>
                            </div>
                            <span className={`badge ${job.status === 'payment_completed' ? 'badge-green' : 'badge-blue'}`} style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}>
                              {job.status === 'payment_completed' ? 'PAID' : 'AWAITING PAYMENT'}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginBottom: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {job.pickup_address}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                              {job.completed_at ? new Date(job.completed_at).toLocaleDateString() : new Date(job.created_at).toLocaleDateString()}
                            </div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#10b981' }}>
                              KES {Number(job.total_amount).toLocaleString()}
                            </div>
                          </div>
                          <button
                            className="btn btn-primary btn-sm"
                            style={{ width: '100%', padding: '0.5rem', fontSize: '0.8rem' }}
                            onClick={() => {
                              setSelectedJob(job)
                              setShowJobDetailsModal(true)
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'earnings' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-label">Total Earnings</div>
                  <div className="stat-value">KES {totalEarnings.toLocaleString()}</div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    All time earnings
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>
                    <Calendar size={24} />
                  </div>
                  <div className="stat-label">This Week</div>
                  <div className="stat-value">
                    KES {completedJobs
                      .filter(j => {
                        const weekAgo = new Date()
                        weekAgo.setDate(weekAgo.getDate() - 7)
                        return new Date(j.created_at) >= weekAgo
                      })
                      .reduce((sum, j) => sum + Number(j.total_amount), 0)
                      .toLocaleString()}
                  </div>
                  <div className="stat-trend">
                    <BarChart3 size={14} />
                    Last 7 days
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
                    <Star size={24} />
                  </div>
                  <div className="stat-label">Completed Jobs</div>
                  <div className="stat-value">{completedJobs.length}</div>
                  <div className="stat-trend">
                    <CheckCircle size={14} />
                    Total completed
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>
                    <TrendingUp size={24} />
                  </div>
                  <div className="stat-label">Avg per Job</div>
                  <div className="stat-value">
                    KES {completedJobs.length > 0 ? Math.round(totalEarnings / completedJobs.length).toLocaleString() : '0'}
                  </div>
                  <div className="stat-trend">
                    <Activity size={14} />
                    Average earning
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="card-title">Earnings History</h2>
                {completedJobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                    <DollarSign size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No earnings yet</p>
                  </div>
                ) : (
                  <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Service Type</th>
                        <th>Date</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedJobs.map(job => (
                        <tr key={job.id}>
                          <td style={{ fontWeight: 600 }}>{job.reference}</td>
                          <td>{job.service_type.replace('_', ' ')}</td>
                          <td>{new Date(job.created_at).toLocaleDateString()}</td>
                          <td style={{ fontWeight: 600 }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'profile' && (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {/* Personal Details */}
              <div className="card" style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Users size={24} color="white" />
                    </div>
                    <div>
                      <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Personal Details</h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>Your personal information</p>
                    </div>
                  </div>
                  <button
                    onClick={openEditPersonal}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.color = 'var(--blue)' }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)' }}
                  >
                    <Wrench size={16} />
                    Edit
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Name</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.full_name || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone Number</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.phone || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck Type</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.truck_type || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck Plate</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.truck_plate || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Business Details */}
              <div className="card" style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Building2 size={24} color="white" />
                    </div>
                    <div>
                      <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Business Details</h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>Your business information</p>
                    </div>
                  </div>
                  <button
                    onClick={openEditBusiness}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.color = 'var(--blue)' }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)' }}
                  >
                    <Wrench size={16} />
                    Edit
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Name</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_name || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Registration</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_registration || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tax PIN</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.tax_pin || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Address</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_address || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Phone</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_phone || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Email</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_email || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Banking & Payment Details */}
              <div className="card" style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CreditCard size={24} color="white" />
                    </div>
                    <div>
                      <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Banking & Payment</h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>Your payment information</p>
                    </div>
                  </div>
                  <button
                    onClick={openEditBanking}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.color = 'var(--blue)' }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)' }}
                  >
                    <Wrench size={16} />
                    Edit
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bank Name</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.bank_name || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bank Account Number</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.bank_account_number || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>M-Pesa Number</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.mpesa_number || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'reports' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Tower Reports</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Truck size={24} style={{ color: 'var(--blue)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Total Jobs</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{jobs.length}</div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Star size={24} style={{ color: 'var(--green)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Avg Rating</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>4.5</div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <DollarSign size={24} style={{ color: 'var(--red)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Earnings</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>KES 0</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                <BarChart3 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <div style={{ fontSize: '1rem', fontWeight: 500 }}>More reports coming soon</div>
                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Detailed analytics and charts will be available here</div>
              </div>
            </div>
          )}

          {tab === 'rates' && (
            <div>
              <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <DollarSign size={24} color="white" />
                    </div>
                    <div>
                      <h2 className="card-title" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Service Rates</h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        Set competitive rates for your services. Market guidance helps you stay profitable. Fuel delivery uses Base + Per-KM pricing.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={openEditRatesModal}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.color = 'var(--blue)' }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)' }}
                  >
                    <Wrench size={16} />
                    Edit Rates
                  </button>
                </div>
              </div>

              {loadingMarketRates ? (
                <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ display: 'inline-block', padding: '1rem', borderRadius: '50%', background: 'var(--bg-secondary)', marginBottom: '1rem' }}>
                    <TrendingUpIcon size={32} style={{ color: 'var(--blue)' }} />
                  </div>
                  <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Loading market rates...</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Base Towing Rate */}
                  <div className="card" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Truck size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem' }}>Base Towing Rate</h3>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>Fixed charge for any towing service</p>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }} className="service-rates-grid">
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                          Your Rate (KES)
                        </label>
                        <input
                          type="number"
                          value={serviceRates.base_towing_rate}
                          readOnly
                          style={{
                            width: '100%',
                            padding: '0.875rem',
                            border: '2px solid var(--border)',
                            borderRadius: '0.75rem',
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            color: 'var(--text)',
                            background: 'var(--bg-secondary)',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                      <div style={{
                        padding: '1rem',
                        borderRadius: '0.75rem',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Market Average
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>
                          KES {marketRates.base_towing_rate.avg}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          Range: {marketRates.base_towing_rate.min} - {marketRates.base_towing_rate.max}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      background: getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'good' ? 'rgba(34, 197, 94, 0.1)' :
                               getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'low' ? 'rgba(245, 158, 11, 0.1)' :
                               getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'high' ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-secondary)',
                      border: `1px solid ${getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'good' ? 'var(--green)' :
                                      getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'low' ? 'var(--amber)' :
                                      getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'high' ? 'var(--red)' : 'var(--border)'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'good' && <CheckCircle size={16} style={{ color: 'var(--green)' }} />}
                        {getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'low' && <AlertCircle size={16} style={{ color: 'var(--amber)' }} />}
                        {getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'high' && <AlertCircle size={16} style={{ color: 'var(--red)' }} />}
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'good' ? 'var(--green)' :
                                getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'low' ? 'var(--amber)' :
                                getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).status === 'high' ? 'var(--red)' : 'var(--text-secondary)'
                        }}>
                          {getRateStatus(serviceRates.base_towing_rate, marketRates.base_towing_rate).message}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Per Kilometer Rate */}
                  <div className="card" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Navigation size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem' }}>Per Kilometer Rate</h3>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>Additional charge per kilometer traveled</p>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }} className="service-rates-grid">
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                          Your Rate (KES)
                        </label>
                        <input
                          type="number"
                          value={serviceRates.per_km_rate}
                          readOnly
                          style={{
                            width: '100%',
                            padding: '0.875rem',
                            border: '2px solid var(--border)',
                            borderRadius: '0.75rem',
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            color: 'var(--text)',
                            background: 'var(--bg-secondary)',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                      <div style={{
                        padding: '1rem',
                        borderRadius: '0.75rem',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Market Average
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>
                          KES {marketRates.per_km_rate.avg}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          Range: {marketRates.per_km_rate.min} - {marketRates.per_km_rate.max}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      background: getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'good' ? 'rgba(34, 197, 94, 0.1)' :
                               getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'low' ? 'rgba(245, 158, 11, 0.1)' :
                               getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'high' ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-secondary)',
                      border: `1px solid ${getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'good' ? 'var(--green)' :
                                      getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'low' ? 'var(--amber)' :
                                      getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'high' ? 'var(--red)' : 'var(--border)'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'good' && <CheckCircle size={16} style={{ color: 'var(--green)' }} />}
                        {getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'low' && <AlertCircle size={16} style={{ color: 'var(--amber)' }} />}
                        {getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'high' && <AlertCircle size={16} style={{ color: 'var(--red)' }} />}
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'good' ? 'var(--green)' :
                                getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'low' ? 'var(--amber)' :
                                getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).status === 'high' ? 'var(--red)' : 'var(--text-secondary)'
                        }}>
                          {getRateStatus(serviceRates.per_km_rate, marketRates.per_km_rate).message}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Battery Service Rate */}
                  <div className="card" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Zap size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem' }}>Battery Service Rate</h3>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>Charge for battery jump-start or replacement</p>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }} className="service-rates-grid">
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                          Your Rate (KES)
                        </label>
                        <input
                          type="number"
                          value={serviceRates.battery_service_rate}
                          readOnly
                          style={{
                            width: '100%',
                            padding: '0.875rem',
                            border: '2px solid var(--border)',
                            borderRadius: '0.75rem',
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            color: 'var(--text)',
                            background: 'var(--bg-secondary)',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                      <div style={{
                        padding: '1rem',
                        borderRadius: '0.75rem',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Market Average
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>
                          KES {marketRates.battery_service_rate.avg}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          Range: {marketRates.battery_service_rate.min} - {marketRates.battery_service_rate.max}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      background: getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'good' ? 'rgba(34, 197, 94, 0.1)' :
                               getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'low' ? 'rgba(245, 158, 11, 0.1)' :
                               getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'high' ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-secondary)',
                      border: `1px solid ${getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'good' ? 'var(--green)' :
                                      getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'low' ? 'var(--amber)' :
                                      getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'high' ? 'var(--red)' : 'var(--border)'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'good' && <CheckCircle size={16} style={{ color: 'var(--green)' }} />}
                        {getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'low' && <AlertCircle size={16} style={{ color: 'var(--amber)' }} />}
                        {getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'high' && <AlertCircle size={16} style={{ color: 'var(--red)' }} />}
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'good' ? 'var(--green)' :
                                getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'low' ? 'var(--amber)' :
                                getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).status === 'high' ? 'var(--red)' : 'var(--text-secondary)'
                        }}>
                          {getRateStatus(serviceRates.battery_service_rate, marketRates.battery_service_rate).message}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Flat Tyre Service Rate */}
                  <div className="card" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <WrenchIcon size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem' }}>Flat Tyre Service Rate</h3>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>Charge for tyre change or repair</p>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }} className="service-rates-grid">
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                          Your Rate (KES)
                        </label>
                        <input
                          type="number"
                          value={serviceRates.flat_tyre_rate}
                          readOnly
                          style={{
                            width: '100%',
                            padding: '0.875rem',
                            border: '2px solid var(--border)',
                            borderRadius: '0.75rem',
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            color: 'var(--text)',
                            background: 'var(--bg-secondary)',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                      <div style={{
                        padding: '1rem',
                        borderRadius: '0.75rem',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Market Average
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>
                          KES {marketRates.flat_tyre_rate.avg}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          Range: {marketRates.flat_tyre_rate.min} - {marketRates.flat_tyre_rate.max}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      background: getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'good' ? 'rgba(34, 197, 94, 0.1)' :
                               getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'low' ? 'rgba(245, 158, 11, 0.1)' :
                               getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'high' ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-secondary)',
                      border: `1px solid ${getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'good' ? 'var(--green)' :
                                      getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'low' ? 'var(--amber)' :
                                      getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'high' ? 'var(--red)' : 'var(--border)'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'good' && <CheckCircle size={16} style={{ color: 'var(--green)' }} />}
                        {getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'low' && <AlertCircle size={16} style={{ color: 'var(--amber)' }} />}
                        {getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'high' && <AlertCircle size={16} style={{ color: 'var(--red)' }} />}
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'good' ? 'var(--green)' :
                                getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'low' ? 'var(--amber)' :
                                getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).status === 'high' ? 'var(--red)' : 'var(--text-secondary)'
                        }}>
                          {getRateStatus(serviceRates.flat_tyre_rate, marketRates.flat_tyre_rate).message}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Fuel Delivery Rate */}
                  <div className="card" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Fuel size={20} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem' }}>Fuel Delivery Rate</h3>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>Charged as: Base Rate + (Distance × Per-KM Rate)</p>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Base Rate</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>KES {serviceRates.fuel_delivery_base_rate.toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Per-KM Rate</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>KES {serviceRates.fuel_delivery_per_km_rate.toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Market Avg (Base)</div>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>KES {marketRates.fuel_delivery_base_rate.avg.toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Market Avg (Per-KM)</div>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>KES {marketRates.fuel_delivery_per_km_rate.avg.toLocaleString()}</div>
                      </div>
                    </div>

                    <div style={{
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)'
                    }}>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        💡 Example: 5km delivery = KES {serviceRates.fuel_delivery_base_rate} + (5 × KES {serviceRates.fuel_delivery_per_km_rate}) = KES {(serviceRates.fuel_delivery_base_rate + 5 * serviceRates.fuel_delivery_per_km_rate).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'services' && (
            <div>
              <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Wrench size={24} color="white" />
                    </div>
                    <div>
                      <h2 className="card-title" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.25rem' }}>My Services</h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        Configure the towing services you offer with custom pricing
                      </p>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={openAddServiceModal}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Plus size={18} />
                    Add Service
                  </button>
                </div>
              </div>

              {towerServices.length === 0 ? (
                <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                  <Wrench size={64} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>No Services Configured</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                    Add your first towing service to start receiving requests
                  </div>
                  <button className="btn btn-primary" onClick={openAddServiceModal}>
                    <Plus size={18} style={{ marginRight: '0.5rem' }} />
                    Add Your First Service
                  </button>
                </div>
              ) : (
                <div className="tower-services-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                  {towerServices.map((service) => (
                    <div key={service.id} style={{
                      background: 'white',
                      borderRadius: '16px',
                      padding: '1.5rem',
                      border: service.is_active ? '2px solid var(--border)' : '2px solid var(--border)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}>
                      {/* Status Badge */}
                      {!service.is_active && (
                        <div style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          background: 'var(--bg-secondary)',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'var(--text-secondary)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Inactive
                        </div>
                      )}

                      {/* Service Icon & Name */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          background: service.is_active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'var(--bg-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Wrench size={28} color={service.is_active ? 'white' : 'var(--text-secondary)'} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 style={{ 
                            fontSize: '1.125rem', 
                            fontWeight: 700, 
                            color: 'var(--text)', 
                            margin: '0 0 0.25rem 0',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {service.service_name}
                          </h3>
                          {service.description && (
                            <p style={{
                              fontSize: '0.8rem',
                              color: 'var(--text-secondary)',
                              margin: 0,
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {service.description}
                            </p>
                          )}
                          {service.vehicle_types && service.vehicle_types.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.5rem' }}>
                              {service.vehicle_types.map((type) => (
                                <span key={type} style={{
                                  fontSize: '0.7rem',
                                  padding: '0.15rem 0.5rem',
                                  background: 'var(--bg)',
                                  color: 'var(--text)',
                                  borderRadius: '0.25rem',
                                  fontWeight: 500
                                }}>
                                  {type}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Pricing Section */}
                      <div style={{
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        borderRadius: '12px',
                        marginBottom: '1rem',
                        border: '1px solid #bae6fd'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Base Price
                          </span>
                          <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0c4a6e' }}>
                            KES {service.base_price.toLocaleString()}
                          </span>
                        </div>
                        {service.per_km_rate > 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#075985', marginTop: '0.25rem' }}>
                            + KES {service.per_km_rate.toLocaleString()} per km
                          </div>
                        )}
                      </div>

                      {/* Requirements Badges */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                        {service.requires_destination && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            padding: '0.4rem 0.75rem',
                            borderRadius: '8px',
                            background: '#dcfce7',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#166534'
                          }}>
                            <MapPin size={14} />
                            Requires Destination
                          </div>
                        )}
                        {service.requires_vehicle && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            padding: '0.4rem 0.75rem',
                            borderRadius: '8px',
                            background: '#fef3c7',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#92400e'
                          }}>
                            <Truck size={14} />
                            Requires Vehicle
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => openEditServiceModal(service)}
                          style={{
                            flex: 1,
                            padding: '0.625rem',
                            borderRadius: '8px',
                            background: 'white',
                            border: '2px solid var(--border)',
                            color: 'var(--text)',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--red)'
                            e.currentTarget.style.color = 'var(--red)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--border)'
                            e.currentTarget.style.color = 'var(--text)'
                          }}
                        >
                          <Edit size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteService(service.id)}
                          style={{
                            padding: '0.625rem',
                            borderRadius: '8px',
                            background: 'var(--red)',
                            border: 'none',
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#dc2626'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--red)'
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Team Management */}
          {tab === 'team' && (
            <div>
              <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Users size={24} color="white" />
                    </div>
                    <div>
                      <h2 className="card-title" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Team Members</h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        Manage staff members who can access your dashboard
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAddTeamMemberModal(true)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                  >
                    <Plus size={18} />
                    Add Team Member
                  </button>
                </div>

                {teamMembers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                    <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No team members yet</p>
                    <p style={{ fontSize: '0.875rem' }}>Add staff members to help manage your operations</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '1rem',
                          background: 'white',
                          borderRadius: '12px',
                          border: '1px solid var(--border)',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'var(--bg)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: 'var(--text-secondary)'
                          }}>
                            {member.profiles?.full_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                              {member.profiles?.full_name || 'Unknown'}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                              {member.profiles?.email || 'No email'}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            background: member.role === 'admin' ? '#fee2e2' : 'var(--bg)',
                            color: member.role === 'admin' ? '#dc2626' : 'var(--text-secondary)'
                          }}>
                            {member.role === 'admin' ? 'Admin' : 'Staff'}
                          </span>
                          <button
                            onClick={() => removeTeamMember(member.user_id)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                              border: 'none',
                              color: 'white',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s',
                              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                          <button
                            onClick={() => resendInvitation(member)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              borderRadius: '8px',
                              background: 'white',
                              border: '2px solid var(--border)',
                              color: 'var(--text)',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626' }}
                            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)' }}
                          >
                            <Mail size={14} />
                            Resend
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add/Edit Service Modal */}
          {showAddServiceModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1.5rem' }}>
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h2>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                      Service Name <span style={{ color: 'var(--red)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={newService.service_name}
                      onChange={(e) => setNewService({ ...newService, service_name: e.target.value })}
                      placeholder="e.g., Vehicle Towing, Item Lifting, Winching"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid var(--border)',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                      Description
                    </label>
                    <textarea
                      value={newService.description}
                      onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                      placeholder="Describe what this service includes..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid var(--border)',
                        fontSize: '0.95rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                      Vehicle Types Applicable
                    </label>
                    <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                      Select the types of vehicles this service can handle
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                      {['Cars', 'Trucks', 'Motorcycles', 'SUVs', 'Vans', 'All Vehicles'].map((type) => (
                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text)', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={newService.vehicle_types?.includes(type) || (type === 'All Vehicles' && newService.vehicle_types?.length === 0)}
                            onChange={(e) => {
                              const currentTypes = newService.vehicle_types || []
                              if (type === 'All Vehicles') {
                                setNewService({ ...newService, vehicle_types: e.target.checked ? ['All Vehicles'] : [] })
                              } else {
                                if (e.target.checked) {
                                  setNewService({ ...newService, vehicle_types: [...currentTypes.filter(t => t !== 'All Vehicles'), type] })
                                } else {
                                  setNewService({ ...newService, vehicle_types: currentTypes.filter(t => t !== type) })
                                }
                              }
                            }}
                            style={{ width: '16px', height: '16px' }}
                          />
                          {type}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                        Base Price (KES) *
                      </label>
                      <input
                        type="number"
                        value={newService.base_price}
                        onChange={(e) => setNewService({ ...newService, base_price: Number(e.target.value) })}
                        placeholder="0"
                        min="0"
                        style={{
                          width: '100%',
                          padding: '0.875rem',
                          border: '2px solid var(--border)',
                          borderRadius: '0.75rem',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                        Per KM Rate (KES)
                      </label>
                      <input
                        type="number"
                        value={newService.per_km_rate}
                        onChange={(e) => setNewService({ ...newService, per_km_rate: Number(e.target.value) })}
                        placeholder="0"
                        min="0"
                        style={{
                          width: '100%',
                          padding: '0.875rem',
                          border: '2px solid var(--border)',
                          borderRadius: '0.75rem',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={newService.requires_destination}
                          onChange={(e) => setNewService({ ...newService, requires_destination: e.target.checked })}
                          style={{ width: '18px', height: '18px' }}
                        />
                        Requires Destination
                      </label>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 0' }}>
                        Customer must provide destination address
                      </p>
                    </div>

                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={newService.requires_vehicle}
                          onChange={(e) => setNewService({ ...newService, requires_vehicle: e.target.checked })}
                          style={{ width: '18px', height: '18px' }}
                        />
                        Requires Vehicle
                      </label>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 0' }}>
                        Customer must provide vehicle information
                      </p>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={newService.is_active}
                        onChange={(e) => setNewService({ ...newService, is_active: e.target.checked })}
                        style={{ width: '18px', height: '18px' }}
                      />
                      Active
                    </label>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 0' }}>
                      Service will be visible to customers when active
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                  <button
                    className="btn btn-outline"
                    onClick={() => {
                      setShowAddServiceModal(false)
                      setEditingService(null)
                    }}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveService}
                    disabled={savingService || !newService.service_name}
                    style={{ flex: 1 }}
                  >
                    {savingService ? 'Saving...' : (editingService ? 'Update Service' : 'Add Service')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Team Member Modal */}
          {showAddTeamMemberModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Add Team Member</h2>
                  <button
                    onClick={() => setShowAddTeamMemberModal(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                  >
                    <X size={24} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newTeamMember.full_name}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, full_name: e.target.value })}
                      placeholder="John Doe"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={newTeamMember.email}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                      placeholder="john@example.com"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Role *
                    </label>
                    <select
                      value={newTeamMember.role}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value as 'admin' | 'staff' })}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem',
                        background: 'white'
                      }}
                    >
                      <option value="staff">Staff - Full access to dashboard</option>
                      <option value="admin">Admin - Full access + can manage team</option>
                    </select>
                  </div>

                  <div style={{
                    padding: '1rem',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <strong>Note:</strong> The team member will be created with a strong temporary password (12+ characters with uppercase, lowercase, numbers, and special characters). They should change it after first login.
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                    <button
                      onClick={() => {
                        setShowAddTeamMemberModal(false)
                        setNewTeamMember({ email: '', full_name: '', role: 'staff' })
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        background: 'white',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--text)',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addTeamMember}
                      disabled={addingTeamMember || !newTeamMember.email || !newTeamMember.full_name}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: addingTeamMember ? 'not-allowed' : 'pointer',
                        opacity: addingTeamMember ? 0.6 : 1,
                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                      }}
                    >
                      {addingTeamMember ? 'Adding...' : 'Add Team Member'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Service Rates Modal */}
          {showEditRatesModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Edit Service Rates</h2>
                  <button
                    onClick={() => setShowEditRatesModal(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                  >
                    <X size={24} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Base Towing Rate (KES)</label>
                    <input
                      type="number"
                      value={editingRates.base_towing_rate}
                      onChange={(e) => setEditingRates({ ...editingRates, base_towing_rate: Number(e.target.value) })}
                      style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Per Kilometer Rate (KES)</label>
                    <input
                      type="number"
                      value={editingRates.per_km_rate}
                      onChange={(e) => setEditingRates({ ...editingRates, per_km_rate: Number(e.target.value) })}
                      style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Battery Service Rate (KES)</label>
                    <input
                      type="number"
                      value={editingRates.battery_service_rate}
                      onChange={(e) => setEditingRates({ ...editingRates, battery_service_rate: Number(e.target.value) })}
                      style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Flat Tyre Service Rate (KES)</label>
                    <input
                      type="number"
                      value={editingRates.flat_tyre_rate}
                      onChange={(e) => setEditingRates({ ...editingRates, flat_tyre_rate: Number(e.target.value) })}
                      style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Fuel Delivery Base Rate (KES)</label>
                    <input
                      type="number"
                      value={editingRates.fuel_delivery_base_rate}
                      onChange={(e) => setEditingRates({ ...editingRates, fuel_delivery_base_rate: Number(e.target.value) })}
                      style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Fuel Delivery Per-KM Rate (KES)</label>
                    <input
                      type="number"
                      value={editingRates.fuel_delivery_per_km_rate}
                      onChange={(e) => setEditingRates({ ...editingRates, fuel_delivery_per_km_rate: Number(e.target.value) })}
                      style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <button
                    onClick={() => setShowEditRatesModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveRatesFromModal}
                    disabled={savingRates}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: 600,
                      cursor: savingRates ? 'not-allowed' : 'pointer',
                      opacity: savingRates ? 0.7 : 1
                    }}
                  >
                    {savingRates ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === 'settings' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Settings</h2>
              <div style={{ maxWidth: '600px' }}>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked />
                    <span>Receive push notifications for new job requests</span>
                  </label>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked />
                    <span>Show availability to customers</span>
                  </label>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input type="checkbox" />
                    <span>Auto-accept jobs within service area</span>
                  </label>
                </div>
                <button className="btn btn-primary">Save Settings</button>
              </div>

              {/* My Roles Section */}
              <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <BadgeCheck size={20} /> My Roles
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
                  Manage your roles on the Mkononi platform. Add or remove roles to access different features.
                </p>

                {roleError && (
                  <div style={{ padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--red)', borderRadius: '0.5rem', marginBottom: '1rem', color: 'var(--red)', fontSize: '0.875rem' }}>
                    {roleError}
                  </div>
                )}

                {/* Current Roles */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)' }}>Current Roles ({userRoles.length})</h4>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {userRoles.map((role) => {
                      const roleConfig = availableRoles.find(r => r.value === role)
                      const Icon = roleConfig?.icon || Truck
                      return (
                        <div
                          key={role}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            background: 'var(--green-light)',
                            border: '1px solid var(--green)',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'var(--green-dark)'
                          }}
                        >
                          <Icon size={16} />
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                          {userRoles.length > 1 && (
                            <button
                              onClick={() => handleRemoveRole(role)}
                              disabled={addingRole}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: addingRole ? 'not-allowed' : 'pointer',
                                padding: '0.1rem',
                                marginLeft: '0.25rem',
                                color: 'var(--red)',
                                opacity: addingRole ? 0.5 : 1
                              }}
                            >
                                <X size={14} />
                              </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Available Roles */}
                <div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)' }}>Add Additional Services</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                    Expand your business by adding more services to your account
                  </p>
                  <button
                    onClick={() => setShowAddRolesModal(true)}
                    disabled={addingRole}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--red)',
                      background: 'var(--red)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: addingRole ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Plus size={16} /> Add Roles
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Switch Role Modal - Always available */}
          {showSwitchRoleModal && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: 'white',
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      maxWidth: '400px',
                      width: '90%'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
                          Switch Role
                        </h2>
                        <button
                          onClick={() => setShowSwitchRoleModal(false)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem'
                          }}
                        >
                          <X size={20} />
                        </button>
                      </div>

                      <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
                        {userRoles.map((role) => {
                          const config = availableRoles.find(r => r.value === role)
                          const Icon = config?.icon || Truck
                          const isCurrent = role === 'tower'
                          return (
                            <button
                              key={role}
                              onClick={() => {
                                if (role !== 'tower') {
                                  // Save preferred role to localStorage
                                  localStorage.setItem('preferredRole', role)
                                  // Reload page to switch dashboard
                                  window.location.reload()
                                }
                              }}
                              disabled={isCurrent}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.75rem',
                                borderRadius: '0.375rem',
                                border: isCurrent ? '2px solid var(--red)' : '1px solid var(--border)',
                                background: isCurrent ? 'rgba(239, 68, 68, 0.05)' : 'white',
                                cursor: isCurrent ? 'not-allowed' : 'pointer',
                                opacity: isCurrent ? 0.7 : 1,
                                transition: 'all 0.2s',
                                textAlign: 'left'
                              }}
                            >
                              <Icon size={18} style={{ color: isCurrent ? 'var(--red)' : 'var(--text)' }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>
                                  {role.charAt(0).toUpperCase() + role.slice(1)}
                                </div>
                              </div>
                              {isCurrent && (
                                <span style={{
                                  padding: '0.125rem 0.375rem',
                                  background: 'var(--red)',
                                  color: 'white',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.7rem',
                                  fontWeight: 500
                                }}>
                                  Current
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setShowSwitchRoleModal(false)}
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '0.375rem',
                            border: '1px solid var(--border)',
                            background: 'white',
                            color: 'var(--text)',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
        </div>
      </main>

      {/* Add Roles Modal - Always available */}
      {showAddRolesModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '450px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
                Add Services
              </h2>
              <button
                onClick={() => setShowAddRolesModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {roleError && (
              <div style={{
                padding: '0.5rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--red)',
                borderRadius: '0.375rem',
                marginBottom: '0.75rem',
                color: 'var(--red)',
                fontSize: '0.8rem'
              }}>
                {roleError}
              </div>
            )}

            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              {availableRoles.map((roleConfig) => {
                const Icon = roleConfig.icon
                const hasThisRole = userRoles.includes(roleConfig.value)
                const isSelected = selectedRolesToAdd.includes(roleConfig.value)

                return (
                  <button
                    key={roleConfig.value}
                    onClick={() => !hasThisRole && toggleRoleSelection(roleConfig.value)}
                    disabled={hasThisRole}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      borderRadius: '0.375rem',
                      border: isSelected ? '2px solid var(--red)' : (hasThisRole ? '1px solid var(--green)' : '1px solid var(--border)'),
                      background: isSelected ? 'rgba(239, 68, 68, 0.05)' : (hasThisRole ? 'var(--green-light)' : 'white'),
                      cursor: hasThisRole ? 'not-allowed' : 'pointer',
                      opacity: hasThisRole ? 0.6 : 1,
                      transition: 'all 0.2s',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '3px',
                      border: isSelected ? '2px solid var(--red)' : '1px solid var(--border)',
                      background: isSelected ? 'var(--red)' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {isSelected && <CheckCircle size={14} color="white" />}
                    </div>
                    <Icon size={18} style={{ color: isSelected ? 'var(--red)' : 'var(--text)' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>
                        {roleConfig.label}
                      </div>
                    </div>
                    {hasThisRole && (
                      <span style={{
                        padding: '0.125rem 0.375rem',
                        background: 'var(--green)',
                        color: 'white',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        fontWeight: 500
                      }}>
                        ✓
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddRolesModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border)',
                  background: 'white',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAddRoles}
                disabled={selectedRolesToAdd.length === 0 || addingRole}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  background: selectedRolesToAdd.length === 0 || addingRole ? 'var(--muted)' : 'var(--red)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: selectedRolesToAdd.length === 0 || addingRole ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {addingRole ? <Loader2 size={14} className="spin" /> : `Add ${selectedRolesToAdd.length} Role${selectedRolesToAdd.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tower Payment Confirmation Modal */}
      {showPaymentModal && selectedPaymentJob && (
        <TowerPaymentConfirmation
          jobId={selectedPaymentJob.id}
          amount={Number(selectedPaymentJob.total_amount)}
          customerName={customerNames[selectedPaymentJob.customer_id] || 'Customer'}
          jobPaymentMethod={selectedPaymentJob.payment_method}
          onClose={() => {
            setShowPaymentModal(false)
            setSelectedPaymentJob(null)
          }}
          onConfirmationComplete={() => {
            // Reload jobs to get updated status
            supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => {
              setJobs((data as TowJob[]) || [])
              setShowPaymentModal(false)
              setSelectedPaymentJob(null)
            })
          }}
        />
      )}

      {/* Job Details Modal */}
      {showJobDetailsModal && selectedJob && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Job Details</h2>
              <button
                onClick={() => setShowJobDetailsModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={20} color="var(--muted)" />
              </button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Reference</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{selectedJob.reference}</div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Service Type</div>
              <div style={{ fontSize: '1rem' }}>{selectedJob.service_type.replace('_', ' ')}</div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Pickup Location</div>
              <div style={{ fontSize: '1rem' }}>{selectedJob.pickup_address}</div>
            </div>

            {selectedJob.destination_place_name && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Destination</div>
                <div style={{ fontSize: '1rem' }}>{selectedJob.destination_place_name}</div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Amount</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>
                KES {Number(selectedJob.total_amount).toLocaleString()}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Status</div>
              <span className={`badge ${selectedJob.status === 'payment_completed' ? 'badge-green' : 'badge-blue'}`}>
                {selectedJob.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>

            {selectedJob.status === 'payment_completed' && (
              <div style={{
                background: '#ecfdf5',
                border: '1px solid #10b981',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontSize: '0.875rem', color: '#065f46', marginBottom: '0.5rem' }}>
                  <strong>Payment Method:</strong> {selectedJob.payment_method?.toUpperCase() || 'N/A'}
                </div>
                {selectedJob.paid_at && (
                  <div style={{ fontSize: '0.875rem', color: '#065f46' }}>
                    <strong>Paid At:</strong> {new Date(selectedJob.paid_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}

            {selectedJob.status === 'payment_completed' && (
              <button
                onClick={() => {
                  // Download receipt using TowerPaymentConfirmation's download function
                  const receiptData = {
                    reference: selectedJob.reference,
                    customer_name: customerNames[selectedJob.customer_id] || 'Customer',
                    tower_name: profile?.full_name || 'Tower',
                    amount: Number(selectedJob.total_amount),
                    payment_method: selectedJob.payment_method || 'cash',
                    confirmed_at: selectedJob.paid_at || new Date().toISOString()
                  }
                  // Reuse the downloadReceipt function from TowerPaymentConfirmation
                  const doc = new jsPDF()
                  const pageWidth = doc.internal.pageSize.getWidth()
                  
                  doc.setFillColor(220, 38, 38)
                  doc.rect(0, 0, pageWidth, 50, 'F')
                  
                  doc.setFillColor(255, 255, 255)
                  doc.circle(30, 25, 12, 'F')
                  doc.setTextColor(220, 38, 38)
                  doc.setFontSize(14)
                  doc.setFont('helvetica', 'bold')
                  doc.text('Mk', 30, 29, { align: 'center' })
                  
                  doc.setTextColor(255, 255, 255)
                  doc.setFontSize(20)
                  doc.setFont('helvetica', 'bold')
                  doc.text('Mkononi', 50, 22)
                  doc.setFontSize(10)
                  doc.setFont('helvetica', 'normal')
                  doc.text('One breakdown. One connected journey.', 50, 30)
                  
                  doc.setFontSize(14)
                  doc.setFont('helvetica', 'bold')
                  doc.text('OFFICIAL RECEIPT', pageWidth - 20, 35, { align: 'right' })
                  
                  doc.setDrawColor(220, 38, 38)
                  doc.setLineWidth(2)
                  doc.line(20, 55, pageWidth - 20, 55)
                  
                  doc.setTextColor(0, 0, 0)
                  doc.setFontSize(10)
                  doc.setFont('helvetica', 'normal')
                  doc.text(`Receipt #: ${receiptData.reference}`, 20, 65)
                  doc.text(`Date: ${new Date(receiptData.confirmed_at).toLocaleDateString()}`, pageWidth - 20, 65, { align: 'right' })
                  
                  doc.setFillColor(245, 245, 245)
                  doc.roundedRect(20, 75, pageWidth - 40, 30, 3, 3, 'F')
                  
                  doc.setFontSize(11)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(220, 38, 38)
                  doc.text('CUSTOMER', 25, 85)
                  
                  doc.setFontSize(10)
                  doc.setFont('helvetica', 'normal')
                  doc.setTextColor(60, 60, 60)
                  doc.text(receiptData.customer_name, 25, 93)
                  
                  let y = 120
                  doc.setFontSize(11)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(220, 38, 38)
                  doc.text('SERVICE DETAILS', 20, y)
                  y += 10
                  
                  doc.setFillColor(220, 38, 38)
                  doc.rect(20, y, pageWidth - 40, 8, 'F')
                  doc.setTextColor(255, 255, 255)
                  doc.setFontSize(9)
                  doc.setFont('helvetica', 'bold')
                  doc.text('DESCRIPTION', 25, y + 5)
                  doc.text('AMOUNT', pageWidth - 40, y + 5, { align: 'right' })
                  
                  y += 8
                  doc.setDrawColor(200, 200, 200)
                  doc.line(20, y, pageWidth - 20, y)
                  
                  y += 5
                  doc.setTextColor(0, 0, 0)
                  doc.setFontSize(10)
                  doc.setFont('helvetica', 'normal')
                  doc.text('TOWING SERVICE', 25, y + 5)
                  doc.text(`KES ${receiptData.amount.toLocaleString()}`, pageWidth - 40, y + 5, { align: 'right' })
                  
                  y += 10
                  doc.setDrawColor(200, 200, 200)
                  doc.line(20, y, pageWidth - 20, y)
                  
                  y += 8
                  doc.setFontSize(12)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(220, 38, 38)
                  doc.text('TOTAL', 25, y + 5)
                  doc.text(`KES ${receiptData.amount.toLocaleString()}`, pageWidth - 40, y + 5, { align: 'right' })
                  
                  y += 15
                  doc.setDrawColor(200, 200, 200)
                  doc.line(20, y, pageWidth - 20, y)
                  
                  y += 10
                  doc.setFontSize(10)
                  doc.setFont('helvetica', 'normal')
                  doc.setTextColor(60, 60, 60)
                  doc.text(`Payment Method: ${receiptData.payment_method.toUpperCase()}`, 25, y)
                  doc.text(`Payment Status: PAID`, 25, y + 7)
                  doc.text(`Confirmed At: ${new Date(receiptData.confirmed_at).toLocaleString()}`, 25, y + 14)
                  doc.text(`Confirmed By: ${receiptData.tower_name}`, 25, y + 21)
                  
                  y += 20
                  doc.setDrawColor(220, 38, 38)
                  doc.line(20, y, pageWidth - 20, y)
                  
                  // Locations section
                  y += 10
                  doc.setFontSize(11)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(220, 38, 38)
                  doc.text('LOCATIONS', 20, y)
                  y += 10
                  
                  doc.setFontSize(9)
                  doc.setFont('helvetica', 'normal')
                  doc.setTextColor(60, 60, 60)
                  doc.text('Pickup Location:', 25, y)
                  y += 6
                  doc.setFontSize(8)
                  doc.setTextColor(100, 100, 100)
                  doc.text(selectedJob.pickup_address || 'N/A', 25, y)
                  
                  if (selectedJob.destination_place_name) {
                    y += 10
                    doc.setFontSize(9)
                    doc.setTextColor(60, 60, 60)
                    doc.text('Destination:', 25, y)
                    y += 6
                    doc.setFontSize(8)
                    doc.setTextColor(100, 100, 100)
                    doc.text(selectedJob.destination_place_name, 25, y)
                  }
                  
                  // Service notes
                  y += 15
                  doc.setFontSize(11)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(220, 38, 38)
                  doc.text('SERVICE NOTES', 20, y)
                  y += 10
                  
                  doc.setFontSize(9)
                  doc.setFont('helvetica', 'normal')
                  doc.setTextColor(60, 60, 60)
                  doc.text(selectedJob.issue_description || 'towing service request', 25, y)
                  
                  // Service provider section
                  y += 20
                  doc.setFontSize(11)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(220, 38, 38)
                  doc.text('SERVICE PROVIDER', 20, y)
                  y += 10
                  
                  doc.setFontSize(10)
                  doc.setFont('helvetica', 'normal')
                  doc.setTextColor(60, 60, 60)
                  doc.text(receiptData.tower_name, 25, y)
                  y += 6
                  doc.setFontSize(9)
                  doc.setTextColor(100, 100, 100)
                  doc.text('Tower', 25, y)
                  y += 6
                  doc.text('Vehicle: KDA 100A', 25, y)
                  
                  y += 20
                  doc.setDrawColor(220, 38, 38)
                  doc.line(20, y, pageWidth - 20, y)
                  
                  y += 10
                  doc.setFontSize(9)
                  doc.setFont('helvetica', 'normal')
                  doc.setTextColor(100, 100, 100)
                  doc.text('Thank you for choosing Mkononi. Your satisfaction is our priority.', 20, y)
                  y += 6
                  doc.text(`Generated on ${new Date(receiptData.confirmed_at).toLocaleString()}`, 20, y)
                  y += 8
                  doc.setFontSize(8)
                  doc.setTextColor(150, 150, 150)
                  doc.text('This is an official receipt. For inquiries, contact support@mkononi.co.ke', 20, y)
                  
                  doc.save(`Mkononi_Receipt_${receiptData.reference}.pdf`)
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Download size={18} />
                Download Receipt
              </button>
            )}
          </div>
        </div>
      )}

      {/* Edit Personal Details Modal */}
      {showEditPersonalModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Edit Personal Details</h2>
              <button
                onClick={() => setShowEditPersonalModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={24} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Full Name</label>
                <input
                  type="text"
                  value={editingProfile.full_name || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, full_name: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Phone Number</label>
                <input
                  type="tel"
                  value={editingProfile.phone || ''}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters and + sign from being typed
                    if (!/[0-9+]/.test(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: phoneError ? '2px solid var(--red)' : '2px solid var(--border)',
                    borderRadius: '8px'
                  }}
                  placeholder="+2547XXXXXXXXX or 07XXXXXXXXX"
                />
                {phoneError && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--red)', marginTop: '0.25rem' }}>
                    {phoneError}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Truck Type</label>
                <input
                  type="text"
                  value={editingProfile.truck_type || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, truck_type: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Truck Plate</label>
                <input
                  type="text"
                  value={editingProfile.truck_plate || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, truck_plate: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => {
                  setShowEditPersonalModal(false)
                  setPhoneError('')
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'white',
                  border: '2px solid var(--border)',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveProfileChanges}
                disabled={savingProfile || !!phoneError}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: 600,
                  cursor: (savingProfile || phoneError) ? 'not-allowed' : 'pointer',
                  opacity: (savingProfile || phoneError) ? 0.7 : 1
                }}
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Business Details Modal */}
      {showEditBusinessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Edit Business Details</h2>
              <button
                onClick={() => setShowEditBusinessModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={24} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Business Name</label>
                <input
                  type="text"
                  value={editingProfile.business_name || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, business_name: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Business Registration</label>
                <input
                  type="text"
                  value={editingProfile.business_registration || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, business_registration: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Tax PIN</label>
                <input
                  type="text"
                  value={editingProfile.tax_pin || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, tax_pin: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Business Address</label>
                <input
                  type="text"
                  value={editingProfile.business_address || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, business_address: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Business Phone</label>
                <input
                  type="tel"
                  value={editingProfile.business_phone || ''}
                  onChange={(e) => handleBusinessPhoneChange(e.target.value)}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters and + sign from being typed
                    if (!/[0-9+]/.test(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: businessPhoneError ? '2px solid var(--red)' : '2px solid var(--border)',
                    borderRadius: '8px'
                  }}
                  placeholder="+2547XXXXXXXXX or 07XXXXXXXXX"
                />
                {businessPhoneError && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--red)', marginTop: '0.25rem' }}>
                    {businessPhoneError}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Business Email</label>
                <input
                  type="email"
                  value={editingProfile.business_email || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, business_email: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => {
                  setShowEditBusinessModal(false)
                  setBusinessPhoneError('')
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'white',
                  border: '2px solid var(--border)',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveProfileChanges}
                disabled={savingProfile || !!businessPhoneError}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: 600,
                  cursor: (savingProfile || businessPhoneError) ? 'not-allowed' : 'pointer',
                  opacity: (savingProfile || businessPhoneError) ? 0.7 : 1
                }}
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Banking Details Modal */}
      {showEditBankingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Edit Banking & Payment</h2>
              <button
                onClick={() => setShowEditBankingModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={24} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Bank Name</label>
                <input
                  type="text"
                  value={editingProfile.bank_name || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, bank_name: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Bank Account Number</label>
                <input
                  type="text"
                  value={editingProfile.bank_account_number || ''}
                  onChange={(e) => setEditingProfile({ ...editingProfile, bank_account_number: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid var(--border)', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>M-Pesa Number</label>
                <input
                  type="tel"
                  value={editingProfile.mpesa_number || ''}
                  onChange={(e) => handleMpesaChange(e.target.value)}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters and + sign from being typed
                    if (!/[0-9+]/.test(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: mpesaError ? '2px solid var(--red)' : '2px solid var(--border)',
                    borderRadius: '8px'
                  }}
                  placeholder="+2547XXXXXXXXX or 07XXXXXXXXX"
                />
                {mpesaError && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--red)', marginTop: '0.25rem' }}>
                    {mpesaError}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => {
                  setShowEditBankingModal(false)
                  setMpesaError('')
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'white',
                  border: '2px solid var(--border)',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveProfileChanges}
                disabled={savingProfile || !!mpesaError}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: 600,
                  cursor: (savingProfile || mpesaError) ? 'not-allowed' : 'pointer',
                  opacity: (savingProfile || mpesaError) ? 0.7 : 1
                }}
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2.5rem',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <CheckCircle size={40} color="white" />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
              Changes Saved!
            </h2>
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              Your profile has been updated successfully.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      {showStatusConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
              }}>
                <CheckCircle size={32} color="white" />
              </div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' }}>
                Confirm Status Change
              </h2>
              <p style={{ fontSize: '0.95rem', color: '#6b7280' }}>
                Are you sure you want to mark this job as <strong>{confirmingStatus?.replace('_', ' ').toUpperCase()}</strong>?
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  setShowStatusConfirm(false)
                  setConfirmingJobId(null)
                  setConfirmingStatus(null)
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusUpdate}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  border: 'none',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <TeamMemberSuccessModal
        isOpen={showTeamSuccessModal}
        onClose={() => setShowTeamSuccessModal(false)}
        memberData={teamSuccessModalData}
        emailSent={teamSuccessModalData.emailSent}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showTeamErrorModal}
        onClose={() => setShowTeamErrorModal(false)}
        title={teamErrorModalData.title}
        message={teamErrorModalData.message}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmModalData.onConfirm}
        title={confirmModalData.title}
        message={confirmModalData.message}
        confirmText="Remove"
        cancelText="Cancel"
      />

      {/* Remove Role Confirmation Modal */}
      {showRemoveRoleConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
                Remove Role
              </h2>
              <button
                onClick={() => setShowRemoveRoleConfirm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '0.9rem', color: 'var(--text)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Are you sure you want to remove the <strong>{roleToRemove?.charAt(0).toUpperCase() + roleToRemove?.slice(1)}</strong> role? This will revoke access to all features associated with this role.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRemoveRoleConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border)',
                  background: 'white',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveRole}
                disabled={addingRole}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  background: addingRole ? 'var(--muted)' : 'var(--red)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: addingRole ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {addingRole ? <Loader2 size={14} className="spin" /> : 'Remove Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Removal Success Modal */}
      {showRoleRemovalSuccess && roleRemovalSuccessData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'var(--green-light)',
              color: 'var(--green)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <CheckCircle size={32} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
              Role Removed Successfully
            </h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              The <strong>{roleRemovalSuccessData.role.charAt(0).toUpperCase() + roleRemovalSuccessData.role.slice(1)}</strong> role has been removed from your account.
            </p>
            <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
              Refreshing your profile...
            </p>
          </div>
        </div>
      )}

      {/* Add Role Confirmation Modal */}
      {showAddRoleConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
                Add Roles
              </h2>
              <button
                onClick={() => setShowAddRoleConfirm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '0.9rem', color: 'var(--text)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Are you sure you want to add the following role{selectedRolesToAdd.length !== 1 ? 's' : ''} to your account?
              <br /><br />
              <strong>{selectedRolesToAdd.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}</strong>
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddRoleConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border)',
                  background: 'white',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAddRoles}
                disabled={addingRole}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  background: addingRole ? 'var(--muted)' : 'var(--red)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: addingRole ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {addingRole ? <Loader2 size={14} className="spin" /> : 'Add Roles'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Addition Success Modal */}
      {showRoleAddSuccess && roleAddSuccessData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'var(--green-light)',
              color: 'var(--green)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <CheckCircle size={32} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
              Role{roleAddSuccessData.roles.length !== 1 ? 's' : ''} Added Successfully
            </h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              The following role{roleAddSuccessData.roles.length !== 1 ? 's have' : ' has'} been added to your account:
              <br /><br />
              <strong>{roleAddSuccessData.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}</strong>
            </p>
            <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
              Refreshing your profile...
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
